import React from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import {
  resolveSeo,
  buildBreadcrumbs,
  ORG_CONTACT,
  DEFAULT_SEO,
  faqPageLd,
  SITE_URL,
} from "../../seo/seoConfig";
import { usePageMeta } from "../../seo/pageMeta";

export default function Seo() {
  const { pathname } = useLocation();
  const routeSeo = resolveSeo(pathname);
  const pageMeta = usePageMeta();
  const title = pageMeta?.title || routeSeo.title;
  const description = pageMeta?.description || routeSeo.description;
  const noindex = routeSeo.noindex;

  const origin = SITE_URL;
  const canonical = `${origin}${pathname === "/" ? "/" : pathname}`;

  const orgLd = {
    "@context": "https://schema.org",
    "@type": "RealEstateAgent",
    "@id": `${origin}/#organization`,
    name: "Deer Park Estates",
    legalName: ORG_CONTACT.legalName,
    description: DEFAULT_SEO.description,
    url: origin,
    logo: `${origin}/deer-logo.png`,
    image: `${origin}/deerpark_hero.jpg`,
    telephone: ORG_CONTACT.telephone,
    faxNumber: ORG_CONTACT.faxNumber,
    email: ORG_CONTACT.email,
    address: {
      "@type": "PostalAddress",
      streetAddress: ORG_CONTACT.streetAddress,
      addressLocality: ORG_CONTACT.addressLocality,
      addressRegion: ORG_CONTACT.addressRegion,
      postalCode: ORG_CONTACT.postalCode,
      addressCountry: ORG_CONTACT.addressCountry,
    },
    areaServed: { "@type": "City", name: "Spruce Grove, Alberta" },
    parentOrganization: {
      "@type": "Organization",
      name: ORG_CONTACT.legalName,
    },
  };

  const crumbs = buildBreadcrumbs(pathname);
  const breadcrumbLd =
    crumbs.length > 1
      ? {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: crumbs.map((c, i) => ({
            "@type": "ListItem",
            position: i + 1,
            name: c.name,
            item: `${origin}${c.path}`,
          })),
        }
      : null;

  const faqLd = pathname.replace(/\/+$/, "") === "/contact" ? faqPageLd() : null;

  return (
    <Helmet>
      <title>{title}</title>
      <link rel="canonical" href={canonical} />
      {noindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow" />
      )}

      <meta name="description" content={description} />

      {/* Open Graph */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />

      {/* Twitter */}
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />

      {/* Structured data */}
      <script type="application/ld+json">{JSON.stringify(orgLd)}</script>
      {breadcrumbLd && (
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbLd)}
        </script>
      )}
      {faqLd && (
        <script type="application/ld+json">{JSON.stringify(faqLd)}</script>
      )}
    </Helmet>
  );
}
